import { useEffect, useRef } from 'react';
import { useGlobals } from '@shared/contexts/GlobalsContext';

interface UseChargebeePortalOptions {
  onError?: () => void;
}

interface UseChargebeePortalResult {
  chargebeeInstance: unknown;
  hasError: boolean;
}

const noop = () => {};

/**
 * Hook to safely access Chargebee client and track if portal has been opened.
 * Returns chargebee instance and error status if client is unavailable.
 */
export function useChargebeePortal({
  onError = noop,
}: UseChargebeePortalOptions = {}): UseChargebeePortalResult {
  const { errorLogger } = useGlobals();
  const portalOpenedRef = useRef(false);
  const hasErrorRef = useRef(false);
  const chargebeeInstanceRef = useRef<unknown>(null);

  useEffect(() => {
    if (portalOpenedRef.current || hasErrorRef.current) {
      return;
    }

    try {
      const chargebee = window.Chargebee?.getInstance?.();
      if (!chargebee) {
        hasErrorRef.current = true;
        onError();
        return;
      }

      chargebeeInstanceRef.current = chargebee;
      portalOpenedRef.current = true;
    } catch (error) {
      hasErrorRef.current = true;
      onError();
      errorLogger.log(error as Error, {
        hook: 'useChargebeePortal',
        action: 'getInstance initialization',
        component: 'subscription',
        severity: 'high',
      });
    }
  }, [onError, errorLogger]);

  return {
    chargebeeInstance: chargebeeInstanceRef.current,
    hasError: hasErrorRef.current,
  };
}
